import { useQuery } from '@tanstack/react-query';
import { adminApi, type AdminPendingSummary } from '@/api/admin';
import { useSession } from '@/state/session';

/**
 * Shared admin "needs attention" counts query.
 *
 * Both the TopNav badge and the AdminHome summary cards read from the same
 * cache key so a single network request feeds both consumers. Refetches on
 * mount/route change (default react-query behavior) plus a lightweight 30s
 * interval while the elevated admin is logged in. Disabled for everyone else
 * so we never expose the endpoint shape to non-admin sessions.
 */
export function useAdminPendingSummary() {
  const user = useSession((s) => s.user);
  const enabled = !!(user?.isAdmin && user.sessionElevated);

  return useQuery<AdminPendingSummary>({
    queryKey: ['admin', 'pending-summary'],
    queryFn: adminApi.pendingSummary,
    enabled,
    refetchInterval: enabled ? 30_000 : false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

/** Sum of all three counts. Convenience for the nav badge. */
export function pendingSummaryTotal(s: AdminPendingSummary | undefined): number {
  if (!s) return 0;
  return s.pendingUsers + s.unverifiedEntries + s.unpaidEntries;
}
